import { useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import vopayApi from '@/services/api.config';
import useStorage from './useStorage';

const { api } = vopayApi;

export default function useAccountTranslationsOverride({ enabled = true }: { enabled?: boolean } = {}) {
  const [translationsInitialized, setTranslationsInitialized] = useState(false);
  const storage = useStorage();
  const jwt = storage.JWT.getJWT();

  useEffect(() => {
    if (!enabled) return;

    if (!jwt) return;

    changeLanguage(i18n.language);
  }, [i18n.language, jwt, enabled]);

  useEffect(() => {
    if (!enabled) return;
    // just in case something goes wrong, allow the user to use the app without translations
    const timeout = setTimeout(() => {
      console.warn('timeout hit in useTranslationsOverride');
      setTranslationsInitialized(true);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [enabled]);

  const controller = useRef<AbortController | null>(null);

  async function changeLanguage(lang: string) {
    await overrideTranslations(lang);
  }

  async function overrideTranslations(lang: string) {
    if (controller.current) {
      controller.current.abort('cancelled overrideTranslations');
    }

    controller.current = new AbortController();
    const signal = controller.current.signal;

    try {
      const urlRes = await api.fetchAccountTranslationsUrl({
        Product: 'elinx',
        Language: lang,
      });

      if (signal.aborted) {
        return;
      }

      if (!urlRes.Success) {
        throw new Error(urlRes.ErrorMessage);
      }

      // no overrides
      if (!urlRes.Url) {
        setTranslationsInitialized(true);

        return;
      }

      const translationsRes = await fetch(urlRes.Url, {
        signal,
      });

      const json = await translationsRes.json();
      i18n.addResourceBundle(lang, 'translations', json, true, true);
      setTranslationsInitialized(true);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      console.error(err);
    } finally {
      // Only clear if this is still the current controller
      if (controller.current?.signal === signal) {
        controller.current = null;
      }
    }
  }

  return {
    translationsInitialized,
    changeLanguage,
  };
}
